import type { ChartRow, ChartSeries, ChartSpec } from './types';
import { chartTimeMilliseconds } from './timeRange';

type LegacyPoint = {
  time?: string | number;
  value?: number;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  detailText?: string;
};

type LegacySeries = {
  name?: string;
  type?: string;
  panel?: string;
  unit?: string;
  color?: string;
  markerShape?: 'arrowUp' | 'arrowDown' | 'circle' | 'square';
  markerPlacement?: 'above' | 'below' | 'at';
  compactMarker?: boolean;
  points?: LegacyPoint[];
};

type SemanticPane = 'price' | 'equity' | 'oscillators';

const DEFAULT_LEGACY_PANELS = new Set(['', 'default', 'default_plot', 'main', 'price']);

function compactSignalLabel(name: string | undefined): string {
  const label = name?.trim() || 'Signal';
  const normalized = label.toLowerCase();
  if (normalized.startsWith('buy ')) return 'Buy';
  if (normalized.startsWith('sell ')) return 'Sell';
  return label;
}

function semanticPaneForSeries(item: LegacySeries): SemanticPane | null {
  const name = String(item.name || '').trim().toLowerCase();
  if (/\b(equity|portfolio value|account value|cash balance|net liquidation)\b/.test(name)) {
    return 'equity';
  }
  if (/\b(rsi\d*|stoch(?:astic)?\d*|williams\s*%?r|cci\d*|mfi\d*|adx\d*)\b/.test(name)) {
    return 'oscillators';
  }
  if (item.type === 'ohlc' || item.type === 'candlestick') return 'price';
  if (/\b(ema|sma|wma|vwap|price|close|open|high|low)\d*\b/.test(name)) return 'price';
  return null;
}

function stableId(prefix: string, index: number): string {
  return `${prefix}_${index + 1}`;
}

export function nearestChartTime(
  rawTime: string | number,
  chartTimes: Array<string | number>,
): string | number {
  const target = chartTimeMilliseconds(rawTime);
  const candidates = chartTimes
    .map((value) => ({ value, millis: chartTimeMilliseconds(value) }))
    .filter((candidate): candidate is { value: string | number; millis: number } => candidate.millis !== null)
    .sort((left, right) => left.millis - right.millis);
  if (target === null || candidates.length === 0) return rawTime;

  const nearest = candidates.reduce((best, candidate) => (
    Math.abs(candidate.millis - target) < Math.abs(best.millis - target)
      ? candidate
      : best
  ));
  const intervals = candidates
    .slice(1)
    .map((candidate, index) => candidate.millis - candidates[index].millis)
    .filter((interval) => interval > 0)
    .sort((left, right) => left - right);
  const medianInterval = intervals.length > 0
    ? intervals[Math.floor(intervals.length / 2)]
    : 0;
  const maximumDistance = Math.max(5 * 60 * 1000, medianInterval);
  return Math.abs(nearest.millis - target) <= maximumDistance
    ? nearest.value
    : rawTime;
}

export function legacyIndicatorDataToChartSpec(
  indicatorData: LegacySeries[],
  signalData: LegacySeries[] = [],
): ChartSpec | null {
  if (!Array.isArray(indicatorData) || indicatorData.length === 0) return null;

  const rowsByTime = new Map<string | number, ChartRow>();
  const paneIds = new Map<string, string>();
  const series: ChartSeries[] = [];
  const primaryPanelName = indicatorData.find((item) => (
    (item.type === 'ohlc' || item.type === 'candlestick') && item.panel
  ))?.panel || indicatorData.find((item) => item.panel)?.panel;
  const panelIdByName = new Map<string, string>();

  const resolvePane = (item: LegacySeries): { id: string; label?: string } => {
    const panelName = String(item.panel || '').trim();
    const shouldInferSemantics = DEFAULT_LEGACY_PANELS.has(panelName.toLowerCase());
    const semanticPane = shouldInferSemantics ? semanticPaneForSeries(item) : null;
    if (semanticPane) {
      const semanticPanes: Record<SemanticPane, { id: string; label: string }> = {
        price: { id: 'primary_pane', label: 'Price' },
        equity: { id: 'equity_pane', label: 'Equity' },
        oscillators: { id: 'oscillator_pane', label: 'Oscillators' },
      };
      return semanticPanes[semanticPane];
    }
    if (!panelName || panelName === primaryPanelName) {
      return { id: 'primary_pane', label: panelName || 'Price' };
    }
    const existing = panelIdByName.get(panelName);
    if (existing) return { id: existing, label: panelName };
    const id = `indicator_pane_${panelIdByName.size + 1}`;
    panelIdByName.set(panelName, id);
    return { id, label: panelName };
  };

  indicatorData.forEach((item, index) => {
    const points = Array.isArray(item.points) ? item.points : [];
    const id = stableId('series', index);
    const pane = resolvePane(item);
    const paneId = pane.id;
    if (!paneIds.has(paneId)) paneIds.set(paneId, pane.label || paneId);
    const type = item.type;
    const isCandlestick = type === 'ohlc' || type === 'candlestick';
    const dataKey = `value_${index + 1}`;
    const fieldMapping = {
      open: `open_${index + 1}`,
      high: `high_${index + 1}`,
      low: `low_${index + 1}`,
      close: `close_${index + 1}`,
    };

    points.forEach((point) => {
      if (point.time === undefined || point.time === null) return;
      const row = rowsByTime.get(point.time) || { time: point.time };
      if (isCandlestick) {
        row[fieldMapping.open] = Number(point.open);
        row[fieldMapping.high] = Number(point.high);
        row[fieldMapping.low] = Number(point.low);
        row[fieldMapping.close] = Number(point.close);
      } else {
        row[dataKey] = Number(point.value);
      }
      rowsByTime.set(point.time, row);
    });

    series.push({
      id,
      label: item.name || `Series ${index + 1}`,
      mark: isCandlestick ? 'candlestick' : type === 'bar' ? 'bar' : 'line',
      role: isCandlestick || semanticPaneForSeries(item) === 'price'
        ? 'price'
        : 'indicator',
      paneId,
      axisId: `${paneId}_axis`,
      dataKey: isCandlestick ? undefined : dataKey,
      fieldMapping: isCandlestick ? fieldMapping : undefined,
      style: {},
      unit: item.unit || 'value',
      frequency: 'provided',
      timezone: 'UTC',
      sourceIds: ['backtest-data'],
      showLastValue: true,
    });
  });

  const data = [...rowsByTime.values()].sort((left, right) => {
    const leftTime = chartTimeMilliseconds(left.time);
    const rightTime = chartTimeMilliseconds(right.time);
    if (leftTime !== null && rightTime !== null) {
      return leftTime - rightTime;
    }
    return String(left.time).localeCompare(String(right.time));
  });
  const panes = [...paneIds].map(([id, label]) => ({
    id,
    label: label === id ? undefined : label,
    weight: id === 'primary_pane' ? 4 : id === 'equity_pane' ? 2 : id === 'oscillator_pane' ? 1.5 : 1,
    axisIds: [`${id}_axis`],
  }));
  const chartTimes = data
    .map((row) => row.time)
    .filter((time): time is string | number => time !== undefined && time !== null);
  const markerSeries = series.find((item) => item.mark === 'candlestick') || series.find((item) => item.role === 'price') || series[0];
  const markers = signalData.flatMap((signal, signalIndex) =>
    (signal.points || [])
      .filter((point) => point.time !== undefined && point.time !== null)
      .map((point, pointIndex) => ({
        id: `signal_${signalIndex + 1}_${pointIndex + 1}`,
        paneId: markerSeries.paneId,
        seriesId: markerSeries.id,
        x: nearestChartTime(point.time as string | number, chartTimes),
        value:
          typeof point.value === 'number'
            ? point.value
            : typeof point.close === 'number'
              ? point.close
              : undefined,
        placement: signal.markerPlacement || 'at',
        shape: signal.markerShape || 'circle',
        color: signal.color || '#F59E0B',
        label: signal.compactMarker ? compactSignalLabel(signal.name) : signal.name || 'Signal',
        detail:
          point.detailText ||
          (signal.compactMarker && compactSignalLabel(signal.name) !== (signal.name || 'Signal')
            ? signal.name
            : undefined),
        compact: signal.compactMarker,
      })),
  );

  return {
    kind: 'timeseries',
    title: 'Strategy indicators',
    xAxis: { key: 'time', type: 'time' },
    axes: panes.map((pane) => ({
      id: pane.axisIds[0],
      side: 'right',
      format: 'number',
    })),
    panes,
    series,
    data,
    referenceLines: [],
    markers,
  };
}
